import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import PasswordInput from "@/components/PasswordInput";
import { BrandmarkHorizontal } from "@/components/Brandmark";

function formatErr(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return err?.message || "Something went wrong";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const next = location.state?.next || searchParams.get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(next);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-black tracking-tight">Welcome back</h1>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
        Log in to access your projects and projects shared with you.
      </p>

      <Button
        type="button"
        variant="outline"
        onClick={loginWithGoogle}
        className="w-full h-11 mt-6 rounded-md border-slate-300 hover:border-slate-950 text-slate-800 font-medium"
        data-testid="login-google-btn"
      >
        <GoogleLogo size={16} weight="bold" className="mr-2" />
        Continue with Google
      </Button>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-sf-label">or with email</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="login-email"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">Password</Label>
            <Link
              to="/forgot-password"
              className="text-[11px] text-slate-500 hover:text-slate-950 dark:hover:text-neon underline-offset-2 hover:underline"
              data-testid="login-forgot-link"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
            data-testid="login-password"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-slate-950 hover:bg-slate-800 text-white rounded-md font-medium"
          data-testid="login-submit"
        >
          {loading ? "Signing in…" : (
            <span className="flex items-center gap-2 justify-center">
              Log In <ArrowRight size={14} weight="bold" />
            </span>
          )}
        </Button>
      </form>

      <p className="mt-6 text-sm text-slate-500 text-center">
        Don&apos;t have an account?{" "}
        <Link to="/register" className="text-slate-950 font-semibold hover:underline" data-testid="go-to-register">
          Create one
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen w-full bg-slate-50 flex flex-col">
      {/* Auth top nav — full-width white band so the nav background matches
          the page edges even on widescreens (the inner content stays
          max-w-[1600px] centered). */}
      <div className="w-full bg-white border-b border-slate-200">
        <div className="h-16 px-8 flex items-center justify-between max-w-[1600px] mx-auto w-full">
          <Link to="/" className="flex items-center group">
            <BrandmarkHorizontal height={32} />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-xs uppercase tracking-widest text-slate-600 hover:text-slate-950"
              style={{ fontFamily: 'inherit' }}
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-slate-300 rounded-md shadow-[0_8px_32px_rgba(15,23,42,0.04)] p-8 fade-up">
          {children}
        </div>
      </div>
    </div>
  );
}
